import { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, Globe } from 'lucide-react';

const WHATSAPP_NUMBER = '905305450170';

const transportLabels = {
  akaryakit: 'Akaryakıt Taşımacılığı',
  adr: 'ADR Tehlikeli Madde',
  lpg: 'LPG Taşımacılığı',
  kimyasal: 'Kimyasal Madde',
  diger: 'Diğer',
};

const buildWhatsAppMessage = (data) => {
  const lines = [
    `*Yeni Operasyon Talebi*`,
    ``,
    `*Firma:* ${data.company_name}`,
    `*Yetkili:* ${data.contact_person}`,
    `*Telefon:* ${data.phone}`,
    `*E-posta:* ${data.email}`,
    `*Taşıma Türü:* ${transportLabels[data.transport_type] || data.transport_type}`,
    `*Güzergah:* ${data.route_from} → ${data.route_to}`,
  ];
  if (data.volume) lines.push(`*Hacim:* ${data.volume}`);
  if (data.notes) lines.push(`*Not:* ${data.notes}`);
  return lines.join('\n');
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    company_name: '', contact_person: '', email: '', phone: '',
    transport_type: '', route_from: '', route_to: '', volume: '', notes: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, transport_type: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.company_name || !formData.contact_person || !formData.email || !formData.phone || !formData.transport_type || !formData.route_from || !formData.route_to) {
      toast.error('Lütfen zorunlu alanları doldurun.');
      return;
    }

    const message = buildWhatsAppMessage(formData);
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');

    toast.success('WhatsApp yönlendirmesi açıldı.');
    setFormData({ company_name: '', contact_person: '', email: '', phone: '', transport_type: '', route_from: '', route_to: '', volume: '', notes: '' });
  };

  const contactInfo = [
    { icon: Phone, title: 'Telefon', value: '0530 545 01 70', href: 'tel:+905305450170', description: '7/24 ulaşabilirsiniz' },
    { icon: Mail, title: 'E-posta', value: 'info@askucukler.com', href: 'mailto:info@askucukler.com', description: 'En kısa sürede dönüş yaparız' },
    { icon: MapPin, title: 'Adres', value: 'İbnisina Mah., Güvenlik Cad. No:68', href: '#map', description: 'Derince / Kocaeli' },
    { icon: Clock, title: 'Çalışma Saatleri', value: '7/24 Operasyon', href: null, description: 'Kesintisiz hizmet' },
  ];

  return (
    <div data-testid="contact-page" className="bg-white">
      <section className="page-hero-banner">
        <img src="/images/hero-slide-2.webp" alt="" className="hero-bg" loading="eager" />
        <div className="hero-gradient" />
        <div className="hero-inner">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <span className="inline-block text-[#FF6B2C] text-xs font-semibold tracking-[0.2em] uppercase mb-4">İletişim</span>
            <h1 className="heading-xl text-white mb-4">Operasyonunuzu planlayalım.</h1>
            <p className="text-base text-white/70 leading-relaxed max-w-lg">Size özel çözümü konuşmak için ekibimiz hazır.</p>
          </motion.div>
        </div>
      </section>

      <section className="section-white py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {contactInfo.map((info, index) => {
              const Content = (
                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-6 h-full hover:border-[#0B3A82]/20 hover:shadow-md transition-all duration-300">
                  <div className="icon-box w-11 h-11 mb-4"><info.icon className="w-5 h-5" /></div>
                  <h3 className="font-bold text-[#1A1A2E] mb-1 text-sm">{info.title}</h3>
                  <p className="text-[#1A1A2E] font-medium text-sm">{info.value}</p>
                  <p className="text-xs text-[#94A3B8] mt-1">{info.description}</p>
                </div>
              );
              return (
                <motion.div key={info.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                  {info.href ? (
                    <a href={info.href} className="block" data-testid={`contact-info-${info.title.toLowerCase()}`}>{Content}</a>
                  ) : (
                    <div data-testid={`contact-info-${info.title.toLowerCase()}`}>{Content}</div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-gray section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="section-label"><span>Operasyon Planlama</span></div>
              <h2 className="heading-md text-[#0B3A82] mb-8">Operasyonunuzu birlikte planlayalım.</h2>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="quote-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="company_name" className="text-[#1A1A2E] font-semibold mb-2 block text-sm">Firma Adı *</Label>
                    <Input id="company_name" name="company_name" value={formData.company_name} onChange={handleInputChange}
                      className="form-input" placeholder="Firma adınızı giriniz" required data-testid="input-company-name" />
                  </div>
                  <div>
                    <Label htmlFor="contact_person" className="text-[#1A1A2E] font-semibold mb-2 block text-sm">Yetkili Kişi *</Label>
                    <Input id="contact_person" name="contact_person" value={formData.contact_person} onChange={handleInputChange}
                      className="form-input" placeholder="İsim Soyisim" required data-testid="input-contact-person" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="email" className="text-[#1A1A2E] font-semibold mb-2 block text-sm">E-posta *</Label>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange}
                      className="form-input" placeholder="ornek@firma.com" required data-testid="input-email" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-[#1A1A2E] font-semibold mb-2 block text-sm">Telefon *</Label>
                    <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange}
                      className="form-input" placeholder="0530 000 00 00" required data-testid="input-phone" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="transport_type" className="text-[#1A1A2E] font-semibold mb-2 block text-sm">Taşıma Türü *</Label>
                  <Select onValueChange={handleSelectChange} value={formData.transport_type}>
                    <SelectTrigger className="form-input" data-testid="select-transport-type">
                      <SelectValue placeholder="Taşıma türünü seçiniz" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#E2E8F0]">
                      <SelectItem value="akaryakit">Akaryakıt Taşımacılığı</SelectItem>
                      <SelectItem value="adr">ADR Tehlikeli Madde</SelectItem>
                      <SelectItem value="kimyasal">Kimyasal Madde</SelectItem>
                      <SelectItem value="diger">Diğer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="route_from" className="text-[#1A1A2E] font-semibold mb-2 block text-sm">Çıkış Noktası *</Label>
                    <Input id="route_from" name="route_from" value={formData.route_from} onChange={handleInputChange}
                      className="form-input" placeholder="Şehir / Tesis adı" required data-testid="input-route-from" />
                  </div>
                  <div>
                    <Label htmlFor="route_to" className="text-[#1A1A2E] font-semibold mb-2 block text-sm">Varış Noktası *</Label>
                    <Input id="route_to" name="route_to" value={formData.route_to} onChange={handleInputChange}
                      className="form-input" placeholder="Şehir / Tesis adı" required data-testid="input-route-to" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="volume" className="text-[#1A1A2E] font-semibold mb-2 block text-sm">Tahmini Hacim / Miktar</Label>
                  <Input id="volume" name="volume" value={formData.volume} onChange={handleInputChange}
                    className="form-input" placeholder="Örn: 30.000 Litre / Ayda 5 sefer" data-testid="input-volume" />
                </div>
                <div>
                  <Label htmlFor="notes" className="text-[#1A1A2E] font-semibold mb-2 block text-sm">Ek Notlar</Label>
                  <Textarea id="notes" name="notes" value={formData.notes} onChange={handleInputChange}
                    className="form-input min-h-[120px]" placeholder="Ek bilgi veya özel taleplerinizi buraya yazabilirsiniz" data-testid="input-notes" />
                </div>
                <Button type="submit"
                  className="w-full bg-[#25D366] hover:bg-[#1da851] text-white rounded-full font-semibold py-6 text-base shadow-lg shadow-green-500/20"
                  data-testid="submit-quote-button"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />WhatsApp ile gönder
                </Button>
              </form>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="section-label"><span>Konum</span></div>
              <h2 className="heading-md text-[#0B3A82] mb-8">Bizi ziyaret edin.</h2>

              <div id="map" className="map-container w-full mb-8" style={{ height: '320px' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3019.8!2d29.82!3d40.75!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDQ1JzAwLjAiTiAyOcKwNDknMTIuMCJF!5e0!3m2!1str!2str!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Asküçükler Lojistik Konum"
                />
              </div>

              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-7 mb-6">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-11 h-11 bg-green-50 rounded-xl flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A1A2E] text-sm">WhatsApp ile ulaşın</h3>
                    <p className="text-xs text-[#64748B]">Hızlı iletişim için</p>
                  </div>
                </div>
                <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Merhaba, bilgi almak istiyorum.')}`} target="_blank" rel="noopener noreferrer" className="block">
                  <Button className="w-full bg-[#25D366] hover:bg-[#1da851] text-white rounded-full font-semibold" data-testid="whatsapp-cta-button">
                    <MessageCircle className="w-5 h-5 mr-2" />WhatsApp'tan yazın
                  </Button>
                </a>
              </div>

              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-7">
                <h3 className="text-lg font-bold text-[#0B3A82] mb-5">Hızlı iletişim</h3>
                <div className="space-y-4">
                  <a href="tel:+905305450170" className="flex items-center gap-4 text-[#64748B] hover:text-[#0B3A82] transition-colors group">
                    <div className="w-10 h-10 rounded-xl bg-[#0B3A82]/6 flex items-center justify-center group-hover:bg-[#0B3A82]/10 transition-colors">
                      <Phone className="w-4 h-4 text-[#0B3A82]" />
                    </div>
                    <span className="font-medium">0530 545 01 70</span>
                  </a>
                  <a href="mailto:info@askucukler.com" className="flex items-center gap-4 text-[#64748B] hover:text-[#0B3A82] transition-colors group">
                    <div className="w-10 h-10 rounded-xl bg-[#0B3A82]/6 flex items-center justify-center group-hover:bg-[#0B3A82]/10 transition-colors">
                      <Mail className="w-4 h-4 text-[#0B3A82]" />
                    </div>
                    <span className="font-medium">info@askucukler.com</span>
                  </a>
                  <a href="https://www.askucukler.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 text-[#64748B] hover:text-[#0B3A82] transition-colors group">
                    <div className="w-10 h-10 rounded-xl bg-[#0B3A82]/6 flex items-center justify-center group-hover:bg-[#0B3A82]/10 transition-colors">
                      <Globe className="w-4 h-4 text-[#0B3A82]" />
                    </div>
                    <span className="font-medium">www.askucukler.com</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
